<template>
  <router-link
    :data-sentry-id="`SearchResult-${letterFile.chronoNumber}`"
    class="bg-white rounded-xl p-2 cursor-pointer flex flex-col"
    :to="`/letter-file/${letterFile.chronoNumber}`"
  >
    <div ref="searchResult">
      <div class="flex items-center flex-col lg:flex-row">
        <div class="flex flex-col ml-1 w-full lg:w-6/12 xl:w-7/12 shrink-0">
          <div class="max-lg:overflow-hidden max-lg:text-ellipsis max-lg:whitespace-nowrap">
            <span v-if="positionLabel" class="text-grey-425"> {{ positionLabel }} • {{ currentName }} </span>
            <span v-else class="text-grey-425">
              {{ currentName }}
            </span>
          </div>
          <div class="hidden lg:block">
            <span class="text-lg font-bold">
              {{ name || "Parapheur sans titre" }}
            </span>
            •
            <span class="text-grey-425"> Numéro Chrono {{ chrono }} </span>
          </div>
          <div class="flex flex-col lg:hidden">
            <div>
              <span class="text-lg font-bold">
                {{ name || "Parapheur sans titre" }}
              </span>
            </div>
            <div>
              <span class="text-grey-425"> N° Chrono {{ chrono }} </span>
            </div>
          </div>
        </div>
        <div class="flex items-center max-lg:mt-1 lg:pl-1 grow flex-wrap lg:flex-nowrap w-full">
          <div class="flex flex-wrap gap-1 lg:ml-auto">
            <PBadge
              v-for="(tag, index) in tags"
              :key="index"
              :type="tag.grey ? 'custom' : 'error'"
              :class="{
                'text-brown-cafe-creme-200-hover bg-brown-cafe-creme-950': tag.grey,
              }"
            >
              {{ tag.name }}
            </PBadge>
            <PBadge v-if="letterFile.status === LetterFileStatus.CC" type="custom" class="text-grey-425 bg-grey-850"
              >En Copie</PBadge
            >
            <DueInBadge :letter-file="letterFile" />
          </div>
          <span class="text-grey-200 ml-auto lg:ml-2">{{ formatDate(letterFile.creationDate) }}</span>
          <OhVueIcon :name="arrow" scale="1.3" class="text-blue-france-sun-113 ml-2 lg:ml-8 lg:mr-4" />
        </div>
      </div>
      <div v-if="letterFile.highlight" class="mt-2 lg:w-1/2">
        <div v-if="letterFile.highlight['comments']" class="flex flex-col border border-grey-625 py-1 px-2 gap-1">
          <span class="text-grey-425 italic">
            Commentaire{{ letterFile.highlight["comments"].length > 1 ? "s" : "" }}
          </span>
          <div v-for="(comment, index) of letterFile.highlight['comments']" :key="index">
            {{ comment }}
          </div>
        </div>
        <div v-if="letterFile.highlight['documents.name']" class="flex flex-col gap-1 mt-2">
          <div
            v-for="(doc, index) of letterFile.highlight['documents.name']"
            :key="index"
            class="border border-grey-625 p-2"
          >
            <OhVueIcon :name="getIcon(doc.replaceAll('}$', '').replaceAll('${', ''))" class="mr-1 mt-1v" scale="1.5" />
            {{ doc }}
          </div>
        </div>
        <div v-else-if="letterFile.highlight['documents.name.keyword']" class="flex flex-col gap-1 mt-2">
          <div
            v-for="(doc, index) of letterFile.highlight['documents.name.keyword']"
            :key="index"
            class="border border-grey-625 p-2"
          >
            <OhVueIcon :name="getIcon(doc.replaceAll('}$', '').replaceAll('${', ''))" class="mr-1 mt-1v" scale="1.5" />
            {{ doc }}
          </div>
        </div>
        <div v-if="letterFile.highlight['externalMailNumber']" class="mt-2">
          Numéro de courrier extérieur:
          {{ letterFile.highlight["externalMailNumber"][0] }}
        </div>
        <div v-if="letterFile.highlight['upstream.name']" class="flex flex-col border border-grey-625 py-1 px-2 gap-1">
          <span class="text-grey-425 italic">En amont</span>
          <div v-for="(name, index) of letterFile.highlight['upstream.name']" :key="index">
            {{ name }}
          </div>
        </div>
        <div
          v-if="letterFile.highlight['downstream.name']"
          class="flex flex-col border border-grey-625 py-1 px-2 gap-1"
        >
          <span class="text-grey-425 italic">En aval</span>
          <div v-for="(name, index) of letterFile.highlight['downstream.name']" :key="index">
            {{ name }}
          </div>
        </div>
        <div v-if="letterFile.highlight['folders']" class="flex flex-col border border-grey-625 py-1 px-2 gap-1">
          <span class="text-grey-425 italic">Dossiers</span>
          <div v-for="(name, index) of letterFile.highlight['folders']" :key="index">
            {{ name }}
          </div>
        </div>
        <div v-if="letterFile.highlight['ccUsers.name']" class="flex flex-col border border-grey-625 py-1 px-2 gap-1">
          <span class="text-grey-425 italic">En copie</span>
          <div v-for="(name, index) of letterFile.highlight['ccUsers.name']" :key="index">
            {{ name }}
          </div>
        </div>
        <div v-if="letterFile.highlight['senderOrganization']" class="">
          Référence externe - Organisme:
          {{ letterFile.highlight["senderOrganization"][0] }}
        </div>
        <div v-if="letterFile.highlight['senderName']" class="">
          Référence externe - Nom, prénom:
          {{ letterFile.highlight["senderName"][0] }}
        </div>
        <div v-if="letterFile.highlight['senderRole']" class="">
          Référence externe - Fonction:
          {{ letterFile.highlight["senderRole"][0] }}
        </div>
        <div v-if="letterFile.highlight['mailType']" class="">
          Référence externe - Type de courrier:
          {{ letterFile.highlight["mailType"][0] }}
        </div>
        <div v-if="letterFile.highlight['recipientName']" class="">
          Référence externe - Destinataire du courrier reçu:
          {{ letterFile.highlight["recipientName"][0] }}
        </div>
        <div v-if="letterFile.highlight['manager']" class="">
          Responsable du parapheur:
          {{ letterFile.highlight["manager"][0] }}
        </div>
      </div>
    </div>
  </router-link>
</template>

<script setup lang="ts">
import { PBadge, useIcons } from "@pasta/box-v2";
import dayjs from "dayjs";
import { computed } from "vue";
import { OhVueIcon } from "oh-vue-icons";
import { RiArrowRightLine } from "oh-vue-icons/icons";
import { type LetterFileDto, LetterFileStatus } from "../dto/letter-file.dto";
import { get } from "lodash";
import { useFileIcon } from "../composable/useFileIcon";
import DueInBadge from "./DueInBadge.vue";

interface Props {
  letterFile: LetterFileDto;
}
const props = withDefaults(defineProps<Props>(), {});

const [arrow] = useIcons(RiArrowRightLine);

const positionLabel = computed(() => {
  switch (props.letterFile.status) {
    case LetterFileStatus.UPSTREAM:
      return "En aval";
    case LetterFileStatus.CURRENT:
      return "À mon niveau";
    case LetterFileStatus.DOWNSTREAM:
      return "En amont";
    default:
      return "";
  }
});

const tags = computed(() => {
  const tags = (props.letterFile.tags || []).map(tag => {
    return { name: tag.name, grey: false };
  });

  if (props.letterFile.draft) tags.push({ name: "brouillon", grey: true });
  if (props.letterFile.status === LetterFileStatus.ARCHIVED) tags.push({ name: "archivé", grey: true });
  return tags;
});

function formatDate(date: string) {
  return dayjs(date).format("DD/MM/YYYY");
}

const name = computed(() => {
  return get(props.letterFile, "highlight.name.0", props.letterFile.name);
});
const chrono = computed(() => {
  return get(props.letterFile, "highlight.chronoNumber.0", props.letterFile.chronoNumber);
});
const currentName = computed(() => {
  return get(props.letterFile, ["highlight", "current.name", "0"], props.letterFile.current?.name);
});

const getIcon = useFileIcon();
</script>
